<template>
	<Transition
		:enter-from-class="enterFromClasses"
		:leave-to-class="leaveToClasses"
		:enter-active-class="enterActiveClasses"
		:leave-active-class="leaveActiveClasses"
		class="origin-top"
	>
		<div class="absolute inset-0 flex justify-center items-start z-20 ~pt-48/80 pb-80 px-24 overflow-y-scroll pointer-events-none" v-if="modalOpen" :key="id">
			<div ref="modalContent" class="relative rounded-16 overflow-clip pointer-events-auto my-auto" :class="class">
				<slot></slot>
				<button @click="mutations.closeModal()" class="absolute top-20 right-20 size-32 flex items-center justify-center rounded-full backdrop-blur-[4px] hover:bg-black/10 transition-colors duration-300 z-10" aria-label="Close modal">
				<slot name="close-icon"></slot>
				</button>
			</div>
		</div>
	</Transition>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';
import { store, mutations } from "@js/vue/store.js";
import { lock, unlock } from "@js/body-scroll-lock.js";

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
  class: {
	type: String,
	default: '',
  },
});

const modalOpen = computed(() => {
  return store.openModal == props.id;
});

const handleKeydown = (e) => {
  if (e.key === 'Escape' && modalOpen.value) {
    mutations.closeModal();
  }
};

onMounted(() => {
  if (props.isOpen) {
    mutations.openModal(props.id);
    lock();
  }
  document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
  unlock();
});

const enterFromClasses = ref('');
const leaveToClasses = ref('');
const enterActiveClasses = ref('');
const leaveActiveClasses = ref('');

watch(() => store.openModal, (newVal, oldVal) => {
  // When switching between modals, oldVal and newVal will both have a value.
  // When opening or closing, one of them will be null.
  const isSwitching = oldVal !== null && newVal !== null;

  if (isSwitching) {
    enterFromClasses.value = '';
    leaveToClasses.value = '';
    enterActiveClasses.value = '';
    leaveActiveClasses.value = '';
  } else {
    enterFromClasses.value = 'opacity-0 blur-[5px] scale-95';
    leaveToClasses.value = 'opacity-0 blur-[5px] scale-95';
    enterActiveClasses.value = 'transition-all duration-300 ease-in-out';
    leaveActiveClasses.value = 'transition-all duration-300 ease-in-out';
  }
}, { immediate: true });

const modalContent = ref(null);

watch(modalOpen, async (isOpen) => {
  if (isOpen) {
    await nextTick();
    if (modalContent.value) {
      const firstInput = modalContent.value.querySelector('input, textarea, select');
      if (firstInput) {
        firstInput.focus();
      }
    }
  }
});

</script>
